import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Logo } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, ShieldCheck, Users, DollarSign, Wallet, Trash2, Ban, CheckCircle2, BarChart3, KeyRound } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const $ = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminDashboard() {
  const [authed, setAuthed] = useState(null);
  const [email, setEmail] = useState("admin@vangstechsubs.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ overview: null, creators: [], payouts: [], deletions: [], analytics: [] });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [o, c, p, d, a] = await Promise.all([
        api.get("/admin/overview"), api.get("/admin/creators"),
        api.get("/admin/payout-requests"), api.get("/admin/deletion-requests"), api.get("/admin/analytics"),
      ]);
      setData({ overview: o.data, creators: c.data, payouts: p.data, deletions: d.data, analytics: a.data });
      setAuthed(true);
    } catch (e) { setAuthed(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/login", { email, password });
      await loadAll();
      toast.success("Welcome, admin.");
    } catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  const setPayout = async (id, status) => { try { await api.post(`/admin/payout-requests/${id}/status`, { status }); toast.success(`Marked ${status}`); loadAll(); } catch { toast.error("Failed"); } };
  const actDeletion = async (id, action) => { try { await api.post(`/admin/deletion-requests/${id}/action`, { action }); toast.success(action === "approve" ? "Account deleted" : "Dismissed"); loadAll(); } catch { toast.error("Failed"); } };
  const suspend = async (id, s) => { try { await api.post(`/admin/creators/${id}/suspend`, { suspended: s }); toast.success(s ? "Suspended" : "Restored"); loadAll(); } catch { toast.error("Failed"); } };
  const changePw = async (e) => {
    e.preventDefault(); setPwBusy(true);
    try { await api.post("/admin/change-password", pwForm); toast.success("Password updated"); setPwForm({ current_password: "", new_password: "" }); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setPwBusy(false); }
  };

  if (authed === null) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><Loader2 className="w-8 h-8 text-violet-600 animate-spin" /></div>;

  if (!authed) return (
    <div className="min-h-screen bg-[#FAF9F6] grain flex flex-col">
      <div className="max-w-7xl mx-auto w-full px-4 h-16 flex items-center"><Logo /></div>
      <div className="flex-1 flex items-center justify-center px-4">
        <form onSubmit={login} className="card-pop bg-white p-8 w-full max-w-sm">
          <div className="flex items-center gap-2 mb-5"><ShieldCheck className="w-6 h-6 text-violet-600" /><h1 className="font-display text-2xl font-extrabold">Admin login</h1></div>
          <Label className="text-sm font-semibold mb-1.5 block">Email</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} className="border-2 border-[#111114] rounded-xl mb-4" data-testid="admin-email" />
          <Label className="text-sm font-semibold mb-1.5 block">Password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="border-2 border-[#111114] rounded-xl mb-5" data-testid="admin-password" />
          <Button type="submit" disabled={busy} className="w-full bg-violet-600 text-white btn-pop rounded-full font-bold" data-testid="admin-login-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}</Button>
        </form>
      </div>
    </div>
  );

  const o = data.overview;
  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#111114]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />
        <span className="chip bg-violet-200 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Admin</span>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 className="font-display text-3xl font-extrabold mb-6">Admin dashboard</h1>
        {o && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card icon={Users} label="Creators" value={o.creators} bg="bg-violet-200" />
            <Card icon={Users} label="Members" value={o.members} bg="bg-cyan-200" />
            <Card icon={DollarSign} label="MRR" value={$(o.mrr)} bg="bg-lime-200" />
            <Card icon={Wallet} label={`Platform fee (${o.fee_pct}%)`} value={$(o.platform_fee_total)} bg="bg-amber-200" />
          </div>
        )}
        <Tabs defaultValue="payouts">
          <TabsList className="bg-white border-2 border-[#111114] rounded-xl p-1 mb-6 h-auto flex-wrap">
            <TabsTrigger value="payouts" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white font-semibold px-4" data-testid="admin-tab-payouts">Payouts {o ? `(${o.pending_payouts})` : ""}</TabsTrigger>
            <TabsTrigger value="deletions" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white font-semibold px-4" data-testid="admin-tab-deletions">Deletions {o ? `(${o.pending_deletions})` : ""}</TabsTrigger>
            <TabsTrigger value="creators" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white font-semibold px-4" data-testid="admin-tab-creators">Creators</TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white font-semibold px-4" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white font-semibold px-4" data-testid="admin-tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="payouts">
            {data.payouts.length === 0 ? <Empty text="No payout requests." /> : (
              <div className="space-y-3">
                {data.payouts.map((p) => (
                  <div key={p.id} className="card-pop bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-payout-${p.id}`}>
                    <div>
                      <p className="font-display font-extrabold">{p.creator_name} — {$(p.net_amount)} <span className="text-slate-400 text-sm font-normal">(gross {$(p.amount)} − {$(p.payout_fee)} fee)</span></p>
                      <p className="text-xs text-slate-500 font-mono">PayPal: {p.paypal_email || "—"} · {new Date(p.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="chip bg-amber-200 capitalize">{p.status}</span>
                      <Button onClick={() => setPayout(p.id, "processing")} className="bg-white text-[#111114] btn-pop rounded-full h-8 text-xs font-bold">Processing</Button>
                      <Button onClick={() => setPayout(p.id, "paid")} className="bg-lime-400 text-[#111114] btn-pop rounded-full h-8 text-xs font-bold" data-testid={`admin-pay-${p.id}`}>Mark paid</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="deletions">
            {data.deletions.length === 0 ? <Empty text="No deletion requests." /> : (
              <div className="space-y-3">
                {data.deletions.map((d) => (
                  <div key={d.id} className="card-pop bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-deletion-${d.id}`}>
                    <div><p className="font-semibold">{d.email}</p><p className="text-xs text-slate-500">{d.reason || "No reason"} · <span className="capitalize">{d.status}</span></p></div>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => actDeletion(d.id, "dismiss")} className="bg-white text-[#111114] btn-pop rounded-full h-8 text-xs font-bold">Dismiss</Button>
                      <Button onClick={() => actDeletion(d.id, "approve")} className="bg-rose-500 text-white btn-pop rounded-full h-8 text-xs font-bold" data-testid={`admin-del-approve-${d.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="creators">
            <div className="space-y-3">
              {data.creators.map((c) => (
                <div key={c.id} className="card-pop bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-creator-${c.id}`}>
                  <div>
                    <p className="font-display font-extrabold">{c.display_name} <span className="text-slate-400 text-sm font-normal">@{c.handle}</span> {c.suspended && <span className="chip bg-rose-300 ml-1">Suspended</span>}</p>
                    <p className="text-xs text-slate-500 font-mono">{c.active_subscribers} subs · MRR {$(c.mrr)} · PayPal {c.paypal_email || "—"}</p>
                  </div>
                  {c.suspended
                    ? <Button onClick={() => suspend(c.id, false)} className="bg-lime-400 text-[#111114] btn-pop rounded-full h-8 text-xs font-bold"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Restore</Button>
                    : <Button onClick={() => suspend(c.id, true)} className="bg-white text-rose-600 border-2 border-rose-500 rounded-full h-8 text-xs font-bold" data-testid={`admin-suspend-${c.id}`}><Ban className="w-3.5 h-3.5 mr-1" /> Suspend</Button>}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="analytics">
            {data.analytics.length === 0 ? <Empty text="No data yet." /> : (
              <div className="grid lg:grid-cols-2 gap-6">
                <ChartCard title="Revenue & platform fee (last 8 weeks)">
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={data.analytics} margin={{ left: -10, right: 8, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="revenue" stroke="#7C3AED" fill="#C4B5FD" strokeWidth={2} name="Revenue" />
                      <Area type="monotone" dataKey="fee" stroke="#F59E0B" fill="#FDE68A" strokeWidth={2} name="Platform fee" />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="New subscribers & sales (last 8 weeks)">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.analytics} margin={{ left: -10, right: 8, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="subscribers" fill="#7C3AED" name="Subscribers" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="sales" fill="#22D3EE" name="Sales" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings">
            <form onSubmit={changePw} className="card-pop bg-white p-7 max-w-md">
              <div className="flex items-center gap-2 mb-5"><KeyRound className="w-5 h-5 text-violet-600" /><h2 className="font-display text-lg font-extrabold">Change admin password</h2></div>
              <Label className="text-sm font-semibold mb-1.5 block">Current password</Label>
              <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="border-2 border-[#111114] rounded-xl mb-4" data-testid="admin-current-pw" />
              <Label className="text-sm font-semibold mb-1.5 block">New password</Label>
              <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} minLength={6} className="border-2 border-[#111114] rounded-xl mb-5" data-testid="admin-new-pw" />
              <Button type="submit" disabled={pwBusy} className="bg-violet-600 text-white btn-pop rounded-full font-bold" data-testid="admin-change-pw-btn">{pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}</Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, bg }) {
  return <div className={`card-pop ${bg} p-5`}><div className="flex items-center gap-2 mb-1"><Icon className="w-4 h-4" /><span className="eyebrow">{label}</span></div><p className="font-display text-2xl font-extrabold">{value}</p></div>;
}
function Empty({ text }) {
  return <div className="card-pop bg-white p-12 text-center text-slate-600">{text}</div>;
}
function ChartCard({ title, children }) {
  return <div className="card-pop bg-white p-5"><h3 className="font-display font-extrabold mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-violet-600" /> {title}</h3>{children}</div>;
}
